import axios from 'axios'
import { isRetriableAuthError } from './authErrors.js'
import { clearPrivateSessionArtifacts } from '../utils/sessionUser.js'

export const ACCESS_TOKEN_KEY = 'auth_token'
export const REFRESH_TOKEN_KEY = 'refresh_token'
export const SESSION_PERSISTENT_KEY = 'session_persistent'
export const SESSION_UPDATED_EVENT = 'auth:session-updated'
export const SESSION_EXPIRED_EVENT = 'auth:token-expired'

export function memoryStorage(initial = {}) {
  const map = { ...initial }
  return {
    getItem: (key) => (Object.prototype.hasOwnProperty.call(map, key) ? map[key] : null),
    setItem: (key, value) => {
      map[key] = String(value)
    },
    removeItem: (key) => {
      delete map[key]
    },
  }
}

function defaultStorage() {
  if (typeof localStorage !== 'undefined') return localStorage
  return memoryStorage()
}

export function readBearer(config) {
  const headers = config?.headers
  if (!headers) return ''
  const value = typeof headers.get === 'function'
    ? headers.get('Authorization')
    : headers.Authorization
  return String(value || '').replace(/^Bearer\s+/i, '').trim()
}

export function applyAuthHeader(config, token) {
  const headers = config.headers || {}
  const value = token ? `Bearer ${token}` : ''
  if (typeof headers.set === 'function') {
    if (value) headers.set('Authorization', value)
    else if (typeof headers.delete === 'function') headers.delete('Authorization')
  } else if (value) {
    headers.Authorization = value
  } else {
    delete headers.Authorization
  }
  config.headers = headers
  return config
}

export function getTokenExpiration(jwtToken) {
  try {
    const payload = JSON.parse(atob(String(jwtToken).split('.')[1]))
    return payload.exp ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

export function isJwtExpired(token, skewMs = 5000) {
  if (!token) return true
  const expiration = getTokenExpiration(token)
  if (!expiration) return false
  return Date.now() >= expiration - skewMs
}

export function persistSession(storage, session) {
  if (session?.access_token) storage.setItem(ACCESS_TOKEN_KEY, session.access_token)
  if (session?.refresh_token) storage.setItem(REFRESH_TOKEN_KEY, session.refresh_token)
  storage.setItem(SESSION_PERSISTENT_KEY, 'true')
}

export function clearSession(storage) {
  storage.removeItem(ACCESS_TOKEN_KEY)
  storage.removeItem(REFRESH_TOKEN_KEY)
  storage.removeItem(SESSION_PERSISTENT_KEY)
  clearPrivateSessionArtifacts()
}

export function createSessionController(options = {}) {
  const storage = options.storage || defaultStorage()
  const refresh = options.refresh
  let apiBaseUrl = options.apiBaseUrl || ''
  let refreshPromise = null
  let ending = false
  let refreshCount = 0

  const emit = (name, detail) => {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new CustomEvent(name, { detail }))
  }

  const readAccessToken = () => storage.getItem(ACCESS_TOKEN_KEY) || ''
  const readRefreshToken = () => storage.getItem(REFRESH_TOKEN_KEY) || ''

  const notifySession = (session) => {
    options.onSession?.(session)
    emit(SESSION_UPDATED_EVENT, session)
  }

  const endSession = ({ redirect = true } = {}) => {
    if (ending) return
    ending = true
    refreshPromise = null
    clearSession(storage)
    options.onLogout?.()
    emit(SESSION_EXPIRED_EVENT, {
      willRedirect: redirect,
      message: 'Votre session a expiré. Veuillez vous reconnecter.',
    })
    if (redirect && !options.disableRedirect && typeof window !== 'undefined') {
      const currentPath = window.location.pathname
      if (currentPath !== '/login' && currentPath !== '/register') {
        sessionStorage.setItem('returnUrl', currentPath + window.location.search)
        window.location.href = '/login'
      }
    }
    setTimeout(() => {
      ending = false
    }, 2000)
  }

  const refreshSession = ({ force = false } = {}) => {
    if (refreshPromise) return refreshPromise
    const current = readAccessToken()
    if (!force && current && !isJwtExpired(current)) {
      return Promise.resolve({
        access_token: current,
        refresh_token: readRefreshToken(),
        reused: true,
      })
    }
    const refreshToken = readRefreshToken()
    if (!refreshToken) {
      return Promise.reject(Object.assign(new Error('No refresh token available'), {
        code: 'NO_REFRESH',
      }))
    }

    refreshPromise = (async () => {
      refreshCount += 1
      const data = refresh
        ? await refresh(refreshToken)
        : (await axios.post(`${apiBaseUrl}/api/auth/refresh`, {
          refresh_token: refreshToken,
        })).data
      const accessToken = data?.token || data?.access_token
      if (!accessToken) {
        throw Object.assign(new Error('No access token in refresh response'), {
          code: 'NO_ACCESS',
        })
      }
      const session = {
        access_token: accessToken,
        refresh_token: data.refresh_token || refreshToken,
      }
      persistSession(storage, session)
      notifySession(session)
      return session
    })().finally(() => {
      refreshPromise = null
    })

    return refreshPromise
  }

  const attachTo = (api, attachOptions = {}) => {
    if (attachOptions.apiBaseUrl) apiBaseUrl = attachOptions.apiBaseUrl
    if (attachOptions.refresh) {
      // reserved for tests that already injected refresh
    }
    api.interceptors.request.use((config) => {
      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        const headers = config.headers
        if (headers && typeof headers.delete === 'function') {
          headers.delete('Content-Type')
          headers.delete('content-type')
        } else if (headers) {
          delete headers['Content-Type']
          delete headers['content-type']
        }
      }
      const token = readAccessToken()
      if (token) applyAuthHeader(config, token)
      else applyAuthHeader(config, '')
      return config
    })

    api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config
        if (!originalRequest) return Promise.reject(error)

        if (error.response?.status === 405 && originalRequest.url?.includes('/social/')) {
          const silentError = new Error('Social endpoint not available')
          silentError.response = error.response
          silentError.config = originalRequest
          silentError.is405 = true
          return Promise.reject(silentError)
        }

        const url = originalRequest.url || ''
        if (!isRetriableAuthError(error.response?.status, url)) {
          return Promise.reject(error)
        }

        if (originalRequest._authRetry) {
          endSession()
          return Promise.reject(error)
        }

        if (!readRefreshToken()) {
          endSession()
          return Promise.reject(error)
        }

        originalRequest._authRetry = true
        const usedToken = readBearer(originalRequest)
        const storedToken = readAccessToken()
        try {
          const session = usedToken && storedToken && usedToken !== storedToken && !isJwtExpired(storedToken)
            ? { access_token: storedToken, reused: true }
            : await refreshSession()
          const token = session?.access_token
          if (!token) throw error
          applyAuthHeader(originalRequest, token)
          return api.request(originalRequest)
        } catch (refreshError) {
          endSession()
          return Promise.reject(refreshError)
        }
      },
    )

    return api
  }

  return {
    storage,
    readAccessToken,
    readRefreshToken,
    refreshSession,
    endSession,
    attachTo,
    persistSession: (session) => persistSession(storage, session),
    get refreshCount() {
      return refreshCount
    },
    get isRefreshing() {
      return Boolean(refreshPromise)
    },
    reset() {
      refreshPromise = null
      ending = false
      refreshCount = 0
    },
  }
}

export const sessionController = createSessionController()
